import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
import { useMindlerApiVanillaClient } from "api/apiv2";
import {
  QuestionnaireContent,
  QuestionnaireRequest,
  QuestionnaireRequestApi,
  QuestionnaireTableRow,
  UserAnswer,
  UserAnswerRequestApi,
} from "core/types/index";
import { useQueryErrorLogger } from "hooks";
import { del, get, post, put } from "../services/httpClient";
import {
  QuestionnairesBasicInfo,
  createQuestionnaireDashboardData,
  createQuestionnaireTableData,
  mapQuestionnaireRequest,
  mapUserAnswerRequest,
} from "./questionnaireHelpers";

const { useMutation, useQuery, useQueryClient } = ReactQueryV5;

const QUESTIONNAIRE_BASE = "privacy/data-collection";

const QUESTIONNAIRE_BASIC_KEY = "questionnaireBasic";
const QUESTIONNAIRE_CONTENT_KEY = "questionnaireContent";
const QUESTIONNAIRE_REQUESTS_KEY = "questionnaireRequests";
const USER_ANSWERS_KEY = "userAnswers";
const BULK_USER_ANSWERS_KEY = "bulkUserAnswers";

export const questionnaireKeys = {
  questionnaires: () => [QUESTIONNAIRE_BASIC_KEY] as const,
  content: (clinicalId: string) =>
    [QUESTIONNAIRE_CONTENT_KEY, clinicalId] as const,
  requests: (userId: number | undefined) =>
    [QUESTIONNAIRE_REQUESTS_KEY, userId] as const,
  userAnswers: (args: {
    userId: string | undefined;
    requestId?: string;
    answerRequestType?: string;
  }) => [USER_ANSWERS_KEY, args] as const,
  bulkUserAnswers: (args: {
    userIds: string[];
    requestId?: string;
    answerRequestType?: string;
  }) => [BULK_USER_ANSWERS_KEY, args] as const,
};

export const useGetQuestionnairesQuery = () =>
  useQuery({
    queryKey: questionnaireKeys.questionnaires(),
    queryFn: async () => {
      const res = await get<{ data: { data: QuestionnairesBasicInfo } }>(
        `${QUESTIONNAIRE_BASE}/questionnaires`,
      );
      return res.data.data;
    },
  });

export const useGetQuestionnaireContentQuery = (
  clinicalId: string | undefined,
) =>
  useQuery({
    queryKey: questionnaireKeys.content(clinicalId!),
    queryFn: async () => {
      const res = await get<{ data: { data: QuestionnaireContent } }>(
        `${QUESTIONNAIRE_BASE}/questionnaire/content?clinicalId=${clinicalId}`,
      );
      return res.data.data;
    },
    enabled: !!clinicalId,
  });

const useGetQuestionnaireRequestsRaw = (userId: number | undefined) =>
  useQuery({
    queryKey: questionnaireKeys.requests(userId),
    queryFn: async () => {
      const res = await get<{ data: { data: QuestionnaireRequestApi[] } }>(
        `${QUESTIONNAIRE_BASE}/questionnaire_requests?userId=${userId}`,
      );
      return res.data.data.map(mapQuestionnaireRequest);
    },
    enabled: userId !== undefined,
  });

export const useGetPsychologistNameToQuestionnaire = (
  incomingQuestionnaires: QuestionnaireRequest[] | undefined,
) => {
  const questionnaires = incomingQuestionnaires ?? [];
  const psychologistIds = questionnaires.map(
    (request) => request.requestingPsychologistId,
  );

  const uniquePsychologistIds = [...new Set(psychologistIds)];

  const mindlerApiClient = useMindlerApiVanillaClient();

  const { data: psychologistResults } = useQuery({
    queryKey: ["psychologists", uniquePsychologistIds],
    queryFn: () =>
      Promise.all(
        uniquePsychologistIds.map((id) =>
          mindlerApiClient.psychologists.findByPsychologistId.query({
            psychologistId: Number(id),
          }),
        ),
      ),
    staleTime: 60 * 1000,
    enabled: uniquePsychologistIds.length > 0,
  });

  const mergedWithPsychologist = questionnaires.map((request) => {
    const psychologist = psychologistResults?.find(
      (psych) =>
        Number(psych?.psychologistId) ===
        Number(request.requestingPsychologistId),
    );
    return {
      ...request,
      requestingPsychologistName: psychologist
        ? `${psychologist.user.firstName} ${psychologist.user.lastName}`
        : request.requestingPsychologistId,
    };
  });

  return mergedWithPsychologist;
};

const useGetQuestionnaireRequestsWithPsychologistData = (
  userId: number | undefined,
) => {
  const {
    data: questionnaireData,
    error,
    isFetching,
    isLoading,
  } = useGetQuestionnaireRequestsRaw(userId);

  const data = useGetPsychologistNameToQuestionnaire(questionnaireData);

  return {
    data,
    error,
    isFetching,
    isLoading,
  };
};

export const useGetUserAnswersQuery = (args: {
  userId: string | undefined;
  requestId?: string;
  answerRequestType?: "clinical" | "icbt";
}) =>
  useQuery({
    queryKey: questionnaireKeys.userAnswers(args),
    queryFn: async () => {
      const queryParams = new URLSearchParams({ userId: args.userId! });
      if (args.requestId) queryParams.append("requestId", args.requestId);
      if (args.answerRequestType)
        queryParams.append("answerRequestType", args.answerRequestType);
      const res = await get<{ data: { data: UserAnswerRequestApi[] } }>(
        `${QUESTIONNAIRE_BASE}/user_answer?${queryParams.toString()}`,
      );
      return res.data.data.map(mapUserAnswerRequest);
    },
    enabled: !!args.userId,
  });

export const useGetBulkUserAnswersQuery = (args: {
  userIds: string[];
  requestId?: string;
  answerRequestType?: "clinical" | "icbt";
}) =>
  useQuery({
    queryKey: questionnaireKeys.bulkUserAnswers(args),
    queryFn: async () => {
      const results = await Promise.allSettled(
        args.userIds.map(async (userId) => {
          const queryParams = new URLSearchParams({ userId });
          if (args.requestId) queryParams.append("requestId", args.requestId);
          if (args.answerRequestType)
            queryParams.append("answerRequestType", args.answerRequestType);
          const res = await get<{ data: { data: UserAnswerRequestApi[] } }>(
            `${QUESTIONNAIRE_BASE}/user_answer?${queryParams.toString()}`,
          );
          return { userId, answers: res.data.data.map(mapUserAnswerRequest) };
        }),
      );
      return Object.fromEntries(
        results
          .filter((result) => result.status === "fulfilled")
          .map(({ value: { userId, answers } }) => [userId, answers]),
      ) as Record<string, UserAnswer[]>;
    },
    enabled: args.userIds.length > 0,
  });

export const usePostQuestionnaireRequestsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { userId: string; clinicalId: string }[]) => {
      const res = await post<
        { data: { data: QuestionnaireRequestApi[] } },
        typeof payload
      >(`${QUESTIONNAIRE_BASE}/questionnaire_requests`, payload);
      return res.data.data.map(mapQuestionnaireRequest);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUESTIONNAIRE_REQUESTS_KEY],
      });
    },
  });
};

export const useDeleteQuestionnaireRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      requestId,
      payload,
    }: {
      requestId: string;
      // API requires requestId in both URL path and body
      payload: { requestId: string; userId: string };
    }) => {
      await del<void, typeof payload>(
        `${QUESTIONNAIRE_BASE}/questionnaire_requests/${requestId}`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUESTIONNAIRE_REQUESTS_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [USER_ANSWERS_KEY] });
    },
  });
};

export const useUpdateUserAnswersMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      patientUserId,
      payload,
    }: {
      patientUserId: string;
      payload: { id: string; psychologistSeenAt: Date };
    }) => {
      const res = await put<
        { data: { data: UserAnswerRequestApi[] } },
        typeof payload
      >(`${QUESTIONNAIRE_BASE}/user_answer?userId=${patientUserId}`, payload);
      return res.data.data.map(mapUserAnswerRequest);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUESTIONNAIRE_REQUESTS_KEY],
      });
    },
  });
};

export const useQuestionnaireTableData = ({
  patientUserId,
}: {
  patientUserId?: string | number;
}): {
  data: QuestionnaireTableRow[];
  isFetching: boolean;
  isLoading: boolean;
  errors: {
    questionnaireRequestsError: unknown;
    userAnswerError: unknown;
    questionnaireBasicInfoError: unknown;
  };
  isError: boolean;
} => {
  const { t } = useTranslation();

  const {
    data: questionnaireRequests,
    error: questionnaireRequestsError,
    isFetching: isFetchingQuestionnaireRequests,
    isLoading: isLoadingQuestionnaireRequests,
  } = useGetQuestionnaireRequestsWithPsychologistData(
    patientUserId ? Number(patientUserId) : undefined,
  );

  const {
    data: patientQuestionnaires,
    error: userAnswerError,
    isFetching: isFetchingUserAnswers,
    isLoading: isLoadingUserAnswers,
  } = useGetUserAnswersQuery({
    userId: patientUserId?.toString(),
    answerRequestType: "clinical",
  });

  const {
    data: questionnaireBasicInfo,
    error: questionnaireBasicInfoError,
    isFetching: isFetchingBasicInfo,
    isLoading: isLoadingBasicInfo,
  } = useGetQuestionnairesQuery();

  const errors = useMemo(() => {
    return {
      questionnaireRequestsError,
      userAnswerError,
      questionnaireBasicInfoError,
    };
  }, [
    questionnaireRequestsError,
    userAnswerError,
    questionnaireBasicInfoError,
  ]);

  const isError = useQueryErrorLogger(
    errors,
    "Failed to load data for questionnaires table",
    {
      team: "dna",
      tag: "questionnaires",
      patientUserId,
    },
  );
  const memo = useMemo(
    () =>
      !questionnaireRequests || !questionnaireBasicInfo
        ? []
        : createQuestionnaireTableData({
            questionnaireRequests,
            patientQuestionnaires,
            questionnaireBasicInfo,
            userAnswerError,
            t,
          }),
    [
      questionnaireRequests,
      questionnaireBasicInfo,
      patientQuestionnaires,
      userAnswerError,
      t,
    ],
  );
  return {
    data: memo,
    isFetching:
      isFetchingBasicInfo ||
      isFetchingQuestionnaireRequests ||
      isFetchingUserAnswers,
    isLoading:
      isLoadingBasicInfo ||
      isLoadingQuestionnaireRequests ||
      isLoadingUserAnswers,
    errors,
    isError,
  };
};

export const useQuestionnaireDashboardData = ({
  patientUserId,
}: {
  patientUserId?: string | number;
}) => {
  const {
    data: questionnaireRequests,
    error: questionnaireRequestsError,
    isFetching: isFetchingQuestionnaireRequests,
    isLoading: isLoadingQuestionnaireRequests,
  } = useGetQuestionnaireRequestsRaw(
    patientUserId ? Number(patientUserId) : undefined,
  );

  const {
    data: patientQuestionnaires,
    error: userAnswerError,
    isFetching: isFetchingUserAnswers,
    isLoading: isLoadingUserAnswers,
  } = useGetUserAnswersQuery({
    userId: patientUserId?.toString(),
    answerRequestType: "clinical",
  });

  const {
    data: questionnaireBasicInfo,
    error: questionnaireBasicInfoError,
    isFetching: isFetchingBasicInfo,
    isLoading: isLoadingBasicInfo,
  } = useGetQuestionnairesQuery();

  const errors = useMemo(() => {
    return {
      questionnaireRequestsError,
      userAnswerError,
      questionnaireBasicInfoError,
    };
  }, [
    questionnaireRequestsError,
    userAnswerError,
    questionnaireBasicInfoError,
  ]);

  const isError = useQueryErrorLogger(
    errors,
    "Failed to load data for questionnaires dashboard",
    {
      team: "dna",
      tag: "questionnaires",
      patientUserId,
    },
  );
  const memo = useMemo(
    () =>
      !questionnaireRequests || !questionnaireBasicInfo
        ? {}
        : createQuestionnaireDashboardData({
            questionnaireRequests,
            patientQuestionnaireAnswers: patientQuestionnaires,
            questionnaireBasicInfo,
          }),
    [questionnaireRequests, questionnaireBasicInfo, patientQuestionnaires],
  );
  return {
    answers: memo,
    questionnaires: questionnaireBasicInfo?.questionnaires ?? [],
    isFetching:
      isFetchingBasicInfo ||
      isFetchingQuestionnaireRequests ||
      isFetchingUserAnswers,
    isLoading:
      isLoadingBasicInfo ||
      isLoadingQuestionnaireRequests ||
      isLoadingUserAnswers,
    errors,
    isError,
  };
};

// Exported as alias matching old RTK export name for convenience
export { useGetQuestionnaireRequestsWithPsychologistData as useGetQuestionnaireRequestsQuery };
